import PlantelSelect from "./plantelSelect";
import WappIcono from "../../../components/wappIcono";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { nombreLista } from "./crmLista";

import HttpManager from "../../../services/HttpManager";
import { telefonoBonito } from "../../../utils/telefonoBonito";
import { Bandeja, BandejaRow, NumeroWa, ProspectCatalogs } from "../../../services/v2/prospects.service";
import ClienteView from "./clienteView";
import { MensajesEncontrados } from "./buscarMensajes";
import { cuando, errorTexto, iniciales, nombreCompleto, tonoAvatar, ultimoMensaje, vistaPrevia } from "./salesHelpers";
import { intervaloRespaldo, push } from "./push";
import { AreaOpcion, anchoArea } from "./salesController";
import NuevoChat, { BotonNuevoChat } from "./nuevoChat";
import NumeroSelect, { idsDeNumero } from "./numeroSelect";

/** Los filtros que sirven en "Todas" (los de cada área solo tienen sentido dentro de su bandeja). */
const COMUNES = ["todos", "sin_contestar", "mias", "sin_asignar", "pasados", "resueltas"];

interface Props {
	campusId: number;
	bandejas: Bandeja[];
	/** Departamento elegido ("todas" = todas las del usuario). */
	dept: string;
	inicial?: number | null;
	onSel?: (id: number | null) => void;
	onConteos?: () => void;
	/** Selector de área arriba de la lista (solo si tiene más de una). */
	areas?: AreaOpcion[];
	onArea?: (key: string) => void;
	/** Filtro "Número" (solo Super Admin): los números de esta área. */
	numeros?: NumeroWa[] | null;
}
const POR_TANDA = 50;

/**
 * Bandeja de Caja y Cobros, Kits, Académico o RRHH (Esteban, 2026-10-04): lo que entró por los números del área en los
 * planteles del rol, más lo que otra área le pasó. Misma forma que la lista de Ventas: filtros, lista como WhatsApp y
 * la conversación con lo del área (tomar, resolver, pasar a otra área).
 */
const BandejaView: React.FC<Props> = ({ campusId, bandejas, dept, inicial, onSel, onConteos, areas, onArea, numeros }) => {
	const http = HttpManager.getInstance();
	const actual = bandejas.find((b) => b.dept === dept) || null;
	const filtros = actual?.filtros || (bandejas[0]?.filtros || []).filter((f) => COMUNES.includes(f.key));
	const [filtro, setFiltro] = useState<string>("todos");
	// Filtro "Plantel" (Todos mis planteles = null): las opciones son los planteles de mis bandejas de esta área.
	const [plantel, setPlantel] = useState<number | null>(null);
	const [numero, setNumero] = useState<string>("");
	const numIds = idsDeNumero(numeros, numero);
	const numLlave = numIds.join(",");
	const plantelesOps = (dept === "todas" ? bandejas : bandejas.filter((b) => b.dept === dept)).flatMap((b) => b.planteles || [])
		.filter((p, i, a) => a.findIndex((x) => x.id === p.id) === i);
	const [search, setSearch] = useState<string>("");
	const [q, setQ] = useState<string>("");
	const [rows, setRows] = useState<BandejaRow[]>([]);
	const rowsRef = useRef<BandejaRow[]>([]);
	rowsRef.current = rows;
	const [total, setTotal] = useState<number>(0);
	const [hasMore, setHasMore] = useState<boolean>(false);
	const [buscando, setBuscando] = useState<boolean>(false);
	const [page, setPage] = useState<number>(1);
	const [loading, setLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [sel, setSel] = useState<number | null>(inicial || null);
	// Abierta desde "Mensajes" (buscar): a qué mensaje ir y de qué bandeja es (puede no estar en la lista de personas).
	const [irA, setIrA] = useState<{ prospectId: number; eventId: number; n: number; dept: string; channelId: number | null } | null>(null);
	const [catalogs, setCatalogs] = useState<ProspectCatalogs | null>(null);
	const turno = useRef<number>(0);
	const [nuevoChat, setNuevoChat] = useState<boolean>(false);
	useEffect(() => { if (onSel) onSel(sel); }, [sel]);
	useEffect(() => { http.prospectsService.catalogs(campusId).then((r) => setCatalogs(r.data)).catch(() => null); }, [campusId]);
	// Only when the area changes, never on mount: on mount it wiped the person opened from CRM (?id=N) and nothing opened (2026-10-15).
	const deptPrevio = useRef<string>(dept);
	useEffect(() => {
		if (deptPrevio.current === dept) return;
		deptPrevio.current = dept;
		setFiltro("todos"); setSel(null); setPlantel(null); setNumero("");
	}, [dept]);

	const cargar = useCallback((p: number) => {
		const yo = ++turno.current;
		setLoading(true); setError(null);
		return http.prospectsService.bandejaLista(campusId, dept, filtro, q, p, POR_TANDA, plantel, numIds).then((r) => {
			if (yo !== turno.current) return;
			if (p === 1) setRows(r.data.data);
			else setRows((prev) => [...prev, ...r.data.data.filter((x) => !prev.some((y) => y.id === x.id && y.dept === x.dept))]);
			setTotal(r.data.total); setPage(p); setHasMore(r.data.hasMore ?? p * POR_TANDA < r.data.total);
		}).catch((e) => { if (yo === turno.current) setError(errorTexto(e)); })
			.finally(() => { if (yo === turno.current) setLoading(false); });
	}, [campusId, dept, filtro, q, plantel, numLlave]);
	useEffect(() => { cargar(1); }, [cargar]);
	useEffect(() => {
		const t = setTimeout(() => { const v = search.trim(); if (v !== q && (v.length >= 3 || v === "")) setQ(v); }, 450);
		return () => clearTimeout(t);
	}, [search]);
	// Lo que llegue mientras está abierta: al instante por wapp-push, con consulta lenta de respaldo.
	useEffect(() => {
		const traer = () => { if (document.visibilityState === "visible") cargar(1); };
		let a: any;
		// Las palomitas de toda la escuela solo importan si esa persona está en la lista (12-oct-2026, hora pico).
		const off = push.on("wa", (d) => {
			if (d?.estado && !rowsRef.current.some((r) => r.id === Number(d?.p))) return;
			clearTimeout(a); a = setTimeout(traer, 1500);
		});
		let t: any;
		const ciclo = () => { t = setTimeout(() => { traer(); ciclo(); }, Math.max(30000, intervaloRespaldo())); };
		ciclo();
		return () => { off(); clearTimeout(a); clearTimeout(t); };
	}, [cargar]);
	// La siguiente tanda cuando el final de la lista se asoma.
	const fin = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const el = fin.current;
		if (!el || !hasMore || loading) return;
		const io = new IntersectionObserver((e) => { if (e[0].isIntersecting) cargar(page + 1); }, { rootMargin: "400px" });
		io.observe(el);
		return () => io.disconnect();
	}, [hasMore, loading, page, cargar]);

	const fila = rows.find((r) => r.id === sel) || null;
	const refrescar = () => { cargar(1); if (onConteos) onConteos(); };
	const cerrarBusqueda = () => { setBuscando(false); setSearch(""); setQ(""); };
	const etiqueta = filtros.find((f) => f.key === filtro)?.label || "Todos";
	const sinContestar = (r: BandejaRow) => r.estado === "abierta" && !!r.last_inbound_at && (!r.last_outbound_at || r.last_outbound_at < r.last_inbound_at);
	const nombreDept = (d: string) => bandejas.find((b) => b.dept === d)?.nombre || d;

	return (
		<div className={"ventas" + (sel ? " con-cliente" : "")}>
			<div className="ventas-cuerpo">
				<div className="ventas-riel">
					<button className="ventas-nuevo" onClick={() => setNuevoChat(true)}>+ Nuevo Chat</button>
					<div className="grupo">Filtros</div>
					{filtros.map((f) => (
						<button key={f.key} className={"vista" + (filtro === f.key ? " activa" : "")} onClick={() => { setFiltro(f.key); setSel(null); }}>
							<span>{f.label}</span>
							{f.key === "sin_contestar" ? <span className={"cnt" + ((actual?.sinContestar || 0) > 0 ? " alerta" : "")}>
								{(dept === "todas" ? bandejas.reduce((t, b) => t + (b.sinContestar || 0), 0) : actual?.sinContestar || 0).toLocaleString("es-MX")}</span> : <span />}
						</button>
					))}
				</div>

				<div className="ventas-lista">
					{buscando ? (
						<div className="wl-barra buscando">
							<button className="wl-icono" onClick={cerrarBusqueda} aria-label="Cerrar búsqueda">←</button>
							<input type="text" enterKeyHint="search" placeholder="Buscar nombre, teléfono o mensaje" value={search} aria-label="Buscar" autoFocus
								onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => { if (e.key === "Escape") cerrarBusqueda(); }} />
							{search ? <button className="wl-icono" onClick={() => { setSearch(""); setQ(""); }} aria-label="Borrar">×</button> : null}
						</div>
					) : (
						<div className="wl-barra de-bandeja">
							{areas && areas.length > 1 && onArea ? (
								<select className="wl-sel area" style={{ width: anchoArea(areas, dept) }} value={dept} onChange={(e) => onArea(e.target.value)} aria-label="Área">
									{areas.map((a) => <option key={a.key} value={a.key}>{a.label + (a.n ? " · " + a.n.toLocaleString("es-MX") : "")}</option>)}
								</select>
							) : null}
							<select className="wl-sel filtro" value={filtro} aria-label="Filtro" onChange={(e) => { setFiltro(e.target.value); setSel(null); }}>
								{filtros.map((f) => <option key={f.key} value={f.key}>{f.label + (f.key === filtro && total ? " (" + total.toLocaleString("es-MX") + ")" : "")}</option>)}
							</select>
							<input className="wl-busca-pc" type="search" placeholder="Buscar nombre, teléfono o mensaje" value={search} aria-label="Buscar" onChange={(e) => setSearch(e.target.value)} />
							<button className="wl-icono wl-lupa" onClick={() => setBuscando(true)} aria-label="Buscar">
								<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" fill="none" stroke="currentColor" strokeWidth="2.2" /><path d="M15.5 15.5 21 21" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" /></svg>
							</button>
							<BotonNuevoChat onClick={() => setNuevoChat(true)} />
						</div>
					)}
					{nuevoChat ? (
						<NuevoChat campusId={campusId} dept={dept} deptNombre={actual?.nombre} onClose={() => setNuevoChat(false)}
							opciones={dept === "todas" ? bandejas.map((b) => ({ dept: b.dept, nombre: b.nombre })) : undefined}
							onAbrir={(id) => { setNuevoChat(false); setFiltro("todos"); setQ(""); setSearch(""); setSel(id); refrescar(); }} />
					) : null}
					{!buscando && (plantelesOps.length > 1 || numeros?.length) ? (
						<div className="plantel-fila">
							<PlantelSelect opciones={plantelesOps} value={plantel} onChange={(v) => { setPlantel(v); setSel(null); }} />
							<NumeroSelect numeros={numeros} value={numero} onChange={(v) => { setNumero(v); setSel(null); }} />
						</div>
					) : null}
					{error ? <div className="aviso error" style={{ margin: 10 }}>{error}</div> : null}
					<div className="filas">
						{rows.map((r) => (
							<button key={r.id + r.dept} className={"ventas-fila" + (sel === r.id ? " activa" : "") + (r.no_leidos ? " no-leido" : "")}
								onClick={() => { setSel(r.id); if (r.no_leidos) setRows((rs) => rs.map((x) => (x.id === r.id ? { ...x, no_leidos: 0 } : x))); }}>
								<span className={"av " + tonoAvatar(r.id)} aria-hidden="true">{iniciales(r)}</span>
								<span className="cuerpo">
									<span className="l1">
										<span className="nombre">{nombreLista(r) || nombreCompleto(r)}</span>
										<span className={"cuando" + (r.no_leidos ? " nuevo" : "")}>{cuando(r.last_activity_at)}</span>
									</span>
									<span className="l2">
										{(() => { const vp = vistaPrevia(r.last_message, r.last_message_by); return vp.texto ? <span className={"txt" + (vp.llamada ? " ll" + (vp.llamada === "perdida" ? " perd" : "") : "")}>{vp.llamada ? <WappIcono n={vp.llamada === "perdida" ? "phoneOff" : "phone"} /> : null}{vp.texto}</span> : <span className="txt">{(r.phone_e164 ? telefonoBonito(r.phone_e164) : "")}</span>; })()}
										{(r.no_leidos || 0) > 0 ? <span className="globo" aria-label={r.no_leidos + " sin leer"}>{r.no_leidos}</span>
											: r.no_leidos === -1 ? <span className="globo vacio" aria-label="Marcada como no leída" />
											: sinContestar(r) ? <span className="punto hueco" title="Sin contestar" aria-label="Sin contestar" /> : null}
									</span>
									{dept === "todas" || r.assigned_name ? (
										<span className="l3 meta">
											{dept === "todas" ? <span>{nombreDept(r.dept)}</span> : null}
											{r.assigned_name ? <span>{r.assigned_name.split(" ")[0]}</span> : null}
										</span>
									) : null}
									{r.pasado_at && r.estado === "abierta" ? <span className="l3">Te lo pasó {(r.pasado_por_name || "").split(" ")[0]}{r.pasado_nota ? ": " + r.pasado_nota : ""}</span> : null}
								</span>
							</button>
						))}
						{!loading && !rows.length ? <div className="ventas-vacio">{q ? "Nadie con ese nombre o teléfono." : "Nada en " + etiqueta.toLowerCase() + "."}</div> : null}
						<div ref={fin} className="wl-fin">{loading ? <span className="wl-cargando" aria-label="Cargando" /> : null}</div>
						{q ? <MensajesEncontrados q={q} llave={JSON.stringify([campusId, dept, plantel])} activo={sel}
							pedir={(t) => http.prospectsService.bandejaBuscarMensajes(campusId, dept, t, plantel).then((r) => r.data.resultados)}
							onAbrir={(r) => { setSel(r.prospect_id); setIrA({ prospectId: r.prospect_id, eventId: r.event_id, n: Date.now(), dept: r.dept || dept, channelId: r.channel_id ?? null }); }} /> : null}
					</div>
				</div>

				{sel ? (
					<ClienteView campusId={campusId} prospectId={sel} catalogs={catalogs} onChanged={refrescar} onOpen={setSel}
						volverTexto={actual?.nombre || "Bandejas"} onBack={() => setSel(null)} irAEvento={irA}
						bandeja={fila ? { dept: fila.dept, channelId: fila.bandeja_channel_id, supervisa: !!bandejas.find((b) => b.dept === fila.dept)?.supervisa }
							: irA && irA.prospectId === sel && irA.dept !== "todas" ? { dept: irA.dept, channelId: irA.channelId, supervisa: !!bandejas.find((b) => b.dept === irA.dept)?.supervisa }
							: (dept !== "todas" ? { dept, channelId: null, supervisa: !!actual?.supervisa } : null)} />
				) : <div className="ventas-cliente"><div className="ventas-vacio">Elige una conversación de la lista.</div></div>}
			</div>
		</div>
	);
};

export default BandejaView;
